/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { PictoChartItemData, PictoChartProps } from './pictoChart.type';
import { TrackResizeContainer } from '#UNSAFE_TrackResizeContainer';
import { PictoChartContext } from './PictoChartContext';
import { PictoChartWithDimensions } from './PictoChartWithDimensions';

/**
 * A picto chart displays information using icons to visualize an absolute number or the relative sizes of the different parts of a population.
 */
export function PictoChart<
  K extends string | number,
  D extends PictoChartItemData<K> = PictoChartItemData<K>
>({ width, height, ...props }: PictoChartProps<K, D>) {
  //  TODO: investigate if we need to handle empty items for performance reasons.
  const supportsSelection = !!(props.selectionMode && props.selectionMode !== 'none');

  const isFlowingWidth = width === undefined;
  const isFlowingHeight = height === undefined;

  return (
    <PictoChartContext.Provider value={{ supportsSelection }}>
      <TrackResizeContainer
        width={isFlowingWidth ? 'initial' : width}
        height={isFlowingHeight ? 'initial' : height}>
        {(width, height) => (
          <PictoChartWithDimensions
            width={width}
            height={height}
            isFlowingWidth={isFlowingWidth}
            isFlowingHeight={isFlowingHeight}
            {...props}
          />
        )}
      </TrackResizeContainer>
    </PictoChartContext.Provider>
  );
}
